import { format } from 'date-fns';
import type { SearchDocumentResult } from '@erp/rpc';
import {
  ReferenceEntityType,
  SystemEntity,
  type PaymentTermsSource,
} from '@erp/shared-types';
import { parseDateOnly } from '@/utils/dateUtils';
import { formatCurrency } from '@/utils/formatters';

// Pure value shaping for the invoice field grid, kept out of the components
// so each branch is unit-tested and the JSX stays a flat list of cells.

const SYSTEM_ENTITY_TO_REFERENCE_ENTITY_TYPE: Partial<
  Record<SystemEntity, ReferenceEntityType>
> = {
  [SystemEntity.INTERNAL_PURCHASE_ORDER]: ReferenceEntityType.PurchaseOrder,
  [SystemEntity.AR_SALES_ORDER]: ReferenceEntityType.SalesOrder,
  [SystemEntity.SCM_SHIPMENT]: ReferenceEntityType.Shipment,
};

export const DOCUMENT_TYPE_TO_SYSTEM_ENTITY: Record<
  SearchDocumentResult['documentType'],
  SystemEntity
> = {
  PURCHASE_ORDER: SystemEntity.INTERNAL_PURCHASE_ORDER,
  SALES_ORDER: SystemEntity.AR_SALES_ORDER,
  SHIPMENT: SystemEntity.SCM_SHIPMENT,
};

export interface ReferenceEntity {
  entityType: ReferenceEntityType;
  entityId: string;
}

/**
 * The PO / SO / shipment associations an invoice is matched against, as the
 * entities whose external references count as "linked". Other association
 * targets (emails, suppliers) are not references and are dropped.
 */
export function linkedReferenceEntities(
  associations: readonly {
    entity: { entityType: SystemEntity; entityId: string };
  }[],
): ReferenceEntity[] {
  return associations.flatMap((assoc) => {
    const entityType =
      SYSTEM_ENTITY_TO_REFERENCE_ENTITY_TYPE[assoc.entity.entityType];
    return entityType ? [{ entityType, entityId: assoc.entity.entityId }] : [];
  });
}

/** Invoice total for display; a non-numeric amount is shown as stored. */
export function formatInvoiceTotal(
  totalAmount: string | null | undefined,
  currency: string | null | undefined,
): string {
  if (!totalAmount) return 'N/A';
  const numeric = Number(totalAmount);
  if (!Number.isFinite(numeric)) return totalAmount;
  return formatCurrency(numeric, { currency: currency || 'USD' });
}

/** DATEONLY string → picker value; undefined when unset or unparseable. */
export function dateOnlyValue(
  value: string | null | undefined,
): Date | undefined {
  return value ? (parseDateOnly(value) ?? undefined) : undefined;
}

/** Date picked → DATEONLY string for the API, or null to clear. */
export function toDateOnly(date: Date | undefined): string | null {
  return date ? format(date, 'yyyy-MM-dd') : null;
}

/** Trimmed invoice number to save. A blank field clears the stored number. */
export function invoiceNumberToSave(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** True when the edited number is already what the invoice stores. */
export function invoiceNumberUnchanged(
  next: string | null,
  current: string | null | undefined,
): boolean {
  return next === (current ?? null);
}

/** "Name | Mon d, yyyy h:mm a", or just the time when the user is unknown. */
export function acceptedLabel({
  approvedAt,
  approverName,
}: {
  approvedAt: string | null | undefined;
  approverName: string | null;
}): string | null {
  if (!approvedAt) return null;
  const when = format(new Date(approvedAt), 'MMM d, yyyy h:mm a');
  return approverName ? `${approverName} | ${when}` : when;
}

/**
 * Whether a scheduled-pay-date pick changes the calendar day. Re-picking the
 * same day (or clearing an already-empty date) must not write a row.
 */
export function scheduledDayChanged(
  picked: Date | undefined,
  existingISO: string | null | undefined,
): boolean {
  const next = picked ? picked.toISOString().slice(0, 10) : null;
  const current = existingISO ? existingISO.slice(0, 10) : null;
  return next !== current;
}

// Mirrors NUMERIC_PATTERN in backend/src/core/utils/pay-terms.ts.
const BARE_DAY_COUNT = /^(\d+)(\s*days?)?$/i;

/**
 * Pay terms for display. ERP terms are often a bare day count ("21"), which
 * due-date derivation reads as days from the invoice date, so it is shown as
 * "Net 21". Anything else is shown as stored.
 */
export function formatPayTerms(
  terms: string | null | undefined,
): string | null {
  const trimmed = terms?.trim();
  if (!trimmed) return null;
  const days = BARE_DAY_COUNT.exec(trimmed)?.[1];
  return days ? `Net ${days}` : trimmed;
}

type TermsSource = Exclude<PaymentTermsSource, 'company'>;

const DEFAULT_TERMS_ORDER: readonly PaymentTermsSource[] = [
  'invoice',
  'supplier',
  'company',
];

export interface InvoicePaymentTerms {
  /** The terms the configured precedence reaches first; null when the company default wins. */
  terms: string | null;
  source: PaymentTermsSource | null;
  /** The other side's terms, shown underneath for comparison. */
  other: { terms: string; source: TermsSource } | null;
}

/**
 * Picks the terms to show by the org's `paymentTermsPriority`, the same order
 * `deriveDueDate` uses (invoice, supplier, company when unset). Reaching
 * `company` first means the company default sets the due date. Printed terms
 * are shown as printed; the supplier's ERP terms go through `formatPayTerms`.
 */
export function invoicePaymentTerms(
  invoiceTerms: string | null | undefined,
  supplierTerms: string | null | undefined,
  priority?: readonly PaymentTermsSource[] | null,
): InvoicePaymentTerms {
  const bySource: Record<TermsSource, string | null> = {
    invoice: invoiceTerms?.trim() || null,
    supplier: formatPayTerms(supplierTerms),
  };
  const entries = (['invoice', 'supplier'] as const).flatMap((source) => {
    const terms = bySource[source];
    return terms ? [{ source, terms }] : [];
  });
  // No terms on either side: nothing to compare (the invoice may still carry
  // a printed due date, which this field cannot see).
  if (entries.length === 0) return { terms: null, source: null, other: null };
  const order = priority?.length ? priority : DEFAULT_TERMS_ORDER;
  const winner = order.find(
    (source) => source === 'company' || bySource[source] !== null,
  );
  const primary = entries.find((entry) => entry.source === winner) ?? null;
  return {
    terms: primary?.terms ?? null,
    source: winner ?? null,
    other: entries.find((entry) => entry !== primary) ?? null,
  };
}
